import { useState } from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { Button } from 'components/atoms/button'
import { BaseModal } from 'components/organisms/modals/base-modal'

interface DeleteContactsModalProps {
	open: boolean
	setOpen: (open: boolean) => void
	onConfirm: () => void
	loading: boolean
	contactName?: string
	count?: number
	isSelectAll?: boolean
	/** Human-readable description of who is being deleted, e.g. "3 contacts in VIP matching current filters" */
	selectionDescription?: string
}

export function DeleteContactsModal({
	open,
	setOpen,
	onConfirm,
	loading,
	contactName,
	count = 0,
	isSelectAll,
	selectionDescription,
}: DeleteContactsModalProps) {
	const isSingle = !!contactName || count === 1

	let message: React.ReactNode
	if (contactName) {
		message = (
			<>
				This will permanently remove <span className="text-primary-text font-semibold">{contactName}</span> from your contact list. This action cannot be undone.
			</>
		)
	} else if (selectionDescription) {
		message = (
			<>
				This will permanently remove <span className="text-primary-text font-semibold">{selectionDescription}</span>. This action cannot be undone.
			</>
		)
	} else if (isSelectAll) {
		message = (
			<>
				This will permanently remove <span className="text-primary-text font-semibold">all {count.toLocaleString()} contacts</span>. This action cannot be undone.
			</>
		)
	} else {
		message = (
			<>
				This will permanently remove <span className="text-primary-text font-semibold">{count.toLocaleString()} {count === 1 ? 'contact' : 'contacts'}</span>. This action cannot be undone.
			</>
		)
	}

	return (
		<BaseModal
			open={open}
			setOpen={loading ? undefined : setOpen}
			title={isSingle ? 'Delete Contact' : 'Delete Contacts'}
			size="sm"
			preventClose={loading}
		>
			<div className="flex flex-col gap-5 text-left">
				<p className="text-sm text-secondary-text leading-relaxed">
					{message}
				</p>

				<div className="border-t border-white/[0.06] pt-4 flex gap-3">
					<Button variant="secondary" fullWidth onClick={() => setOpen(false)} disabled={loading}>
						Cancel
					</Button>
					<Button
						variant="primary"
						fullWidth
						onClick={onConfirm}
						disabled={loading}
					>
						{loading ? (
							<>
								<FontAwesomeIcon icon={['fas', 'spinner']} spin className="mr-2" />
								Deleting…
							</>
						) : (
							'Delete'
						)}
					</Button>
				</div>
			</div>
		</BaseModal>
	)
}

// ---------------------------------------------------------------------------
// Modal Manager integration
// ---------------------------------------------------------------------------

export interface DeleteContactsModalManagedProps {
	onConfirm: () => void
	loading: boolean
	contactName?: string
	count?: number
	isSelectAll?: boolean
	selectionDescription?: string
}

declare module 'components/organisms/modals/modal-registry' {
	interface ModalRegistry {
		'delete-contacts': DeleteContactsModalManagedProps
	}
}

export function ManagedDeleteContactsModal({
	onClose,
	onConfirm,
	...rest
}: DeleteContactsModalManagedProps & { onClose: () => void }) {
	const [open, setOpen] = useState(true)

	const handleClose = () => {
		setOpen(false)
		setTimeout(onClose, 200)
	}

	return (
		<DeleteContactsModal
			open={open}
			setOpen={(v: boolean) => { if (!v) handleClose() }}
			onConfirm={() => { onConfirm(); handleClose() }}
			{...rest}
		/>
	)
}
